// Core module: This should only depend on other core modules.

// Background on Dimensional analysis: https://en.wikipedia.org/wiki/Dimensional_analysis
// Inspired by https://github.com/jscheiny/safe-units

import { initializeBuild } from './units/factory.ts';
import {
  Angle,
  Cardinality,
  Currency,
  Length,
  Mass,
  Proportion,
  Repetitions,
  Time,
  type Acceleration,
  type AngularVelocity,
  type Force,
  type Frequency,
  type Impulse,
  type Velocity,
} from './units/quantity.ts';
import { Unit, type UnitOf } from './units/unit.ts';

export { Fraction, Measure } from './units/measure.ts';
export { type IMeta } from './units/meta.ts';
export * from './units/quantity.ts';
export { Scalar } from './units/scalar.ts';
export * from './units/standard.ts';
export { Unit } from './units/unit.ts';
export { Vector } from './units/vector.ts';

initializeBuild();

export const Meters = Unit.create(1, 'meters', 'm', Length.i);
export const Kilometers = Unit.of(1000, Meters, 'kilometers', 'km');
export const Centimeters = Unit.of(0.01, Meters, 'centimeters', 'cm');
export const Millimeters = Unit.of(0.001, Meters, 'millimeters', 'mm');
export const Inches = Unit.of(0.0254, Meters, 'inches', 'in');
export const Feet = Unit.of(12, Inches, 'feet', 'ft');
export const Yards = Unit.of(3, Feet, 'yards', 'yd');
export const Miles = Unit.of(5280, Feet, 'miles', 'mi');

export const Kilograms = Unit.create(1, 'kilograms', 'kg', Mass.i);
export const Grams = Unit.of(0.001, Kilograms, 'grams', 'g');
export const Pounds = Unit.of(0.453592, Kilograms, 'pounds', 'lb');

export const Seconds = Unit.create(1, 'seconds', 's', Time.i);
export const Minutes = Unit.of(60, Seconds, 'minutes', 'min');
export const Hours = Unit.of(60, Minutes, 'hours', 'hr');
export const Days = Unit.of(24, Hours, 'days', 'd');
// TODO: Consider ways to standardize SI prefix modifiers for any unit type.
export const Milliseconds = Unit.of(1e-3, Seconds, 'milliseconds', 'ms');
export const Microseconds = Unit.of(1e-6, Seconds, 'microseconds', 'μs');
export const Nanoseconds = Unit.of(1e-9, Seconds, 'nanoseconds', 'ns');

export const MetersPerSecond: UnitOf<Velocity> = Meters.per(Seconds);
export const MetersPerSecond2: UnitOf<Acceleration> = MetersPerSecond.per(Seconds);

export const Newtons: UnitOf<Force> = Kilograms.dot(MetersPerSecond2);
export const NewtonSeconds: UnitOf<Impulse> = Newtons.dot(Seconds);

export const Radians = Unit.create(1, 'radians', 'rad', Angle.i);
export const Degrees = Unit.of(Math.PI / 180, Radians, 'degrees', '°');

export const RadiansPerSecond: UnitOf<AngularVelocity> = Radians.per(Seconds);
export const DegreesPerSecond: UnitOf<AngularVelocity> = Degrees.per(Seconds);

export const Count = Unit.create(1, 'count', '', Cardinality.i);
export const Rate = Count(1).per(Seconds(1));

export const Percent = Unit.create(100, 'percent', '%', Proportion.i);

export function ratio(numerator: number, denominator: number) {
  const units = Unit.create(1, 'ratio', `${numerator}:${denominator}`, Proportion.i);
  return units(numerator / denominator);
}

export const Cycles = Unit.create(1, 'cycles', 'cyc', Repetitions.i);
export const Hertz: UnitOf<Frequency> = Cycles.per(Seconds);

export const Cents = Unit.create(1, 'cents', '¢', Currency.i);
export const Dollars = Unit.of(100, Cents, 'dollars', '$');

// TODO: uni-directional conversion functions for units like temperature celsius <=> fahrenheit <=> kelvin
